import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CommonApiResponse } from 'src/app/models/common-api-response.model';

enum EndPoints {
  Default = 'users',
  Users_list = 'list',
  UserInfo = 'userinfo',
  Register = 'register',
  MFA_TOTP = 'settings/mfa/totp',
  Reset_MFA_TOTP = 'settings/mfa/totp/reset',
  Activate_MFA_TOTP = 'settings/mfa/totp/activate',
  reset_password = 'settings/change_password',
  reset_admin_user_password = 'settings/change_user_password',
  Datatable = 'datatables',
  Avatar = 'avatar',
  Sessions = 'sessions',
  UserRole= 'users/register ',
  activate = 'activate',
  deactivate = 'deactivate',
  resend_activation = 'resend_activation',
  force_mfa_state = 'force_mfa_state'
}

@Injectable()
export class UserService {
  private baseApiUrl: string;

  constructor(private _httpClient: HttpClient) {
    this.baseApiUrl = environment.base_url;
  }

  private handleError(err: HttpErrorResponse) {
    return throwError(() => ({error: err.error, status: err.status}));
  }
  getDataTable(): Observable<CommonApiResponse> {
    const endpoint = `${this.baseApiUrl}/${EndPoints.Default}/${EndPoints.Users_list}`;
    return this._httpClient.get<CommonApiResponse>(endpoint).pipe(
      catchError(this.handleError)
    );
  }
  getUserInfo(): Observable<CommonApiResponse> {
    const endpoint = `${this.baseApiUrl}/${EndPoints.Default}/${EndPoints.UserInfo}`;
    return this._httpClient.get<CommonApiResponse>(endpoint).pipe(
      catchError(this.handleError)
    );
  }
  getUser(userId: string): Observable<CommonApiResponse> {
    const endpoint = `${this.baseApiUrl}/${EndPoints.Default}/${userId}`;
    return this._httpClient.get<CommonApiResponse>(endpoint).pipe(
      catchError(this.handleError)
    );
  }
  getUserRoles(): Observable<CommonApiResponse> {
    const endpoint = `${this.baseApiUrl}/${EndPoints.UserRole}`;
    return this._httpClient.get<CommonApiResponse>(endpoint).pipe(
      catchError(this.handleError)
    );
  }

  getSessions(): Observable<CommonApiResponse> {
    const endpoint = `${this.baseApiUrl}/${EndPoints.Default}/${EndPoints.Sessions}`;
    return this._httpClient.get<CommonApiResponse>(endpoint).pipe(
      catchError(this.handleError)
    );
  }
  
  createUser(requestBody: any): Observable<CommonApiResponse> {
    const endpoint = `${this.baseApiUrl}/${EndPoints.Default}/${EndPoints.Register}`;
    return this._httpClient.post<CommonApiResponse>(endpoint, requestBody).pipe(
      catchError(this.handleError)
    );
  }
  updateUser(userId: string, requestBody: any): Observable<CommonApiResponse> {
    const endpoint = `${this.baseApiUrl}/${EndPoints.Default}/${userId}`;
    return this._httpClient.put<CommonApiResponse>(endpoint, requestBody).pipe(
      catchError(this.handleError)
    );
  }
  uploadAvatar(formData: FormData): Observable<CommonApiResponse> {
    const endpoint = `${this.baseApiUrl}/${EndPoints.Default}/${EndPoints.Avatar}`;
    return this._httpClient.post<CommonApiResponse>(endpoint, formData).pipe(
      catchError(this.handleError)
    );
  }
  deleteAvatar(): Observable<CommonApiResponse> {
    const endpoint = `${this.baseApiUrl}/${EndPoints.Default}/${EndPoints.Avatar}`;
    return this._httpClient.delete<CommonApiResponse>(endpoint).pipe(
      catchError(this.handleError)
    );
  }

  
  // New bulk operations
  deleteUsers(userIds: string[]): Observable<CommonApiResponse> {
    const endpoint = `${this.baseApiUrl}/${EndPoints.Default}`;
    return this._httpClient.delete<CommonApiResponse>(endpoint, { body: { users: userIds } }).pipe(
      catchError(this.handleError)
    );
  }
  
  deactivateUsers(userIds: string[]): Observable<CommonApiResponse> {
    const endpoint = `${this.baseApiUrl}/${EndPoints.Default}/${EndPoints.deactivate}`;
    return this._httpClient.put<CommonApiResponse>(endpoint, { users: userIds }).pipe(
      catchError(this.handleError)
    );
  }
  

  activateUsers(userIds: string[]): Observable<CommonApiResponse> {
    const endpoint = `${this.baseApiUrl}/${EndPoints.Default}/${EndPoints.activate}`;
    return this._httpClient.put<CommonApiResponse>(endpoint, { users: userIds }).pipe(
      catchError(this.handleError)
    );
  }
  
  resendActivation(userIds: string[]): Observable<CommonApiResponse> {
    const endpoint = `${this.baseApiUrl}/${EndPoints.Default}/${EndPoints.resend_activation}`;
    return this._httpClient.put<CommonApiResponse>(endpoint, { users: userIds }).pipe(
      catchError(this.handleError)
    );
  }
  
  forceMFAState(userIds: string[], status: boolean, forceForAll: boolean = false): Observable<CommonApiResponse> {
    const endpoint = `${this.baseApiUrl}/${EndPoints.Default}/${EndPoints.force_mfa_state}`;
    const payload: any = { users: userIds, status };
    if (forceForAll) {
      payload.force_for_all = true;
    }
    return this._httpClient.put<CommonApiResponse>(endpoint, payload).pipe(
      catchError(this.handleError)
    );
  }
  setMFA_TOTP(requestBody: any): Observable<CommonApiResponse> {
    const endpoint = `${this.baseApiUrl}/${EndPoints.Default}/${EndPoints.MFA_TOTP}`;
    return this._httpClient.put<CommonApiResponse>(endpoint, requestBody).pipe(
      catchError(this.handleError)
    );
  }
  resetMFA_TOTP(requestBody: any): Observable<CommonApiResponse> {
    const endpoint = `${this.baseApiUrl}/${EndPoints.Default}/${EndPoints.Reset_MFA_TOTP}`;
    return this._httpClient.put<CommonApiResponse>(endpoint, requestBody).pipe(
      catchError(this.handleError)
    );
  }
  activateMFA_TOTP(requestBody: any): Observable<CommonApiResponse> {
    const endpoint = `${this.baseApiUrl}/${EndPoints.Default}/${EndPoints.Activate_MFA_TOTP}`;
    return this._httpClient.put<CommonApiResponse>(endpoint, requestBody).pipe(
      catchError(this.handleError)
    );
  }
  change_password(requestBody: any): Observable<CommonApiResponse> {
    const endpoint = `${this.baseApiUrl}/${EndPoints.Default}/${EndPoints.reset_password}`;
    return this._httpClient.put<CommonApiResponse>(endpoint, requestBody).pipe(
      catchError(this.handleError)
    );
  }
  reset_password(requestBody: any): Observable<CommonApiResponse> {
    const endpoint = `${this.baseApiUrl}/${EndPoints.Default}/${EndPoints.reset_admin_user_password}`;
    return this._httpClient.put<CommonApiResponse>(endpoint, requestBody).pipe(
      catchError(this.handleError)
    );
  }
}
